import { AccountResponse } from '@/types/AccountResponse'
import BaseService from './baseService'
import { SearchEngineAuthorization } from '@/types/SearchEngineAuthResponse'
import { BusinessProfileSearchEngineRequest } from '@/types/BusinessProfileSearchEngineRequest'
import { ReAuthRequest } from '@/types/ReAuthRequest'
import EndPoints from '@/constants/dxApiEndpoints'

class AccountService extends BaseService {
  async getAccountDetails(
    businessProfileId: number,
    searchEngineTypeId: number = 0
  ): Promise<Array<AccountResponse>> {
    const queryParams = {}
    if (searchEngineTypeId != 0) {
      queryParams.SearchEngineTypeId = searchEngineTypeId
    }
    const respone = await this.apiClient.get<Array<AccountResponse>>(
      EndPoints.AccountDetails(businessProfileId),
      queryParams
    )
    return respone.data
  }

  async createSegmentBpseMapping(
    businessProfileId: number,
    segmentId: number,
    accountIds: Array<number>
  ) {
    await this.apiClient.post(
      EndPoints.SegmentBusinessProfileSearchEngineMapping(
        businessProfileId,
        segmentId
      ),
      accountIds
    )
  }

  async getSearchEngineAuthorizationsDetails(
    businessProfileId: number,
    accountId: number
  ): Promise<SearchEngineAuthorization> {
    const response = await this.apiClient.get(
      EndPoints.SearchEngineAuthDetails(businessProfileId, accountId)
    )
    return response.data
  }

  async getEmbedAccessToken(
    businessProfileId: number,
    accountId: number
  ): Promise<string> {
    const response = await this.apiClient.get(
      EndPoints.GetEmbedAccessToken(businessProfileId, accountId)
    )
    return response.data
  }

  /**
   * Calls SearchEngineAuthorizationController to fetch auth data from Redis using authKey and insert it into database.
   * @param businessProfileId ID of the business profile
   * @param authKey Authorization key
   */
  async saveSearchEngineAuthorization(
    businessProfileId: number | string,
    authKey: string
  ): Promise<unknown> {
    const url = EndPoints.SearchEngineAuthorization(businessProfileId)
    // Pass authKey in query parameter and request body (camelCase only)
    const response = await this.apiClient.post<unknown>(
      `${url}?authKey=${encodeURIComponent(authKey)}`,
      {
        authKey
      }
    )
    return response
  }

  /**
   * Saves a business profile search engine (BPSE) account using AccountsController.
   * @param businessProfileId ID of the business profile
   * @param request BusinessProfileSearchEngineRequest payload
   */
  async saveAccount(
    businessProfileId: number | string,
    request: BusinessProfileSearchEngineRequest
  ): Promise<unknown> {
    const numericBpId = Number(businessProfileId)
    const response = await this.apiClient.post<unknown>(
      EndPoints.AccountDetails(numericBpId),
      request
    )
    return response
  }

  /**
   * Reauthorizes an existing BPSE account after OAuth reconnect.
   * Calls [HttpPost("{businessProfileSearchEngineId}/reauthorize")].
   * @param businessProfileId ID of the business profile
   * @param businessProfileSearchEngineId ID of the business profile search engine (BPSE ID)
   * @param request ReAuthRequest payload
   */
  async reauthorizeAccount(
    businessProfileId: number | string,
    businessProfileSearchEngineId: number | string,
    request: ReAuthRequest
  ): Promise<unknown> {
    const numericBpId = Number(businessProfileId)
    const numericBpseId = Number(businessProfileSearchEngineId)
    const response = await this.apiClient.post<unknown>(
      EndPoints.ReauthorizeAccount(numericBpId, numericBpseId),
      request
    )
    return response
  }
}

export default AccountService
